import * as React from 'react';
import { useSelector } from 'react-redux';
import { selectAdvertisingApiCsrfToken } from 'src/app/slice';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import {
  advertisingApiClient,
  AdvertisingApiClientProxy,
} from 'src/commons/utils/advertising-api-client';
import contextHookCreator from 'src/commons/utils/context-hook-creator';

type AdvertisingApiClientContextProps = {
  advertisingApiClient: AdvertisingApiClientProxy;
  advertisingApiCsrfToken: string;
};

const { hostname, protocol } = window.location;
const apiEndpointPath = `${protocol}//${hostname}/a9g-api-gateway`;

// converted from `AdvertisingExperienceChromeNavigation` in http://advertising-api-sleuth-na.aka.amazon.com/clientId
const apiClientId =
  'amzn.advertising.789c2d8cdb11002108035bca7a02d27f6302de4f26b379c80d090e256c706460a3429f3c5995adbf50bc8d94d9b907da332c36248e32e6d07adefe7d76e302c12f15c9';

const AdvertisingApiClientContext =
  React.createContext<AdvertisingApiClientContextProps>({
    advertisingApiClient,
    advertisingApiCsrfToken: '',
  });

export const useAdvertisingApiContext =
  contextHookCreator<AdvertisingApiClientContextProps>(
    AdvertisingApiClientContext,
    'useAdvertisingApiContext',
    'AdvertisingApiClientProvider',
  );

export const AdvertisingApiClientProvider: React.FC = ({ children }) => {
  const internalContext = useInternalChromeConfigContext();
  // Need to pass CSRF token to conditionally render the bell drawer
  // only when the CSRF token is available
  const advertisingApiCsrfToken = useSelector(selectAdvertisingApiCsrfToken);
  advertisingApiClient.init({
    clientId: apiClientId,
    csrfToken: advertisingApiCsrfToken,
    // relative path to the domain. see https://tiny.amazon.com/19zujy9kg/codeamazpackAdveblobmainconf
    // maybe this should be passed from the server?
    endpoint: apiEndpointPath,
    entityId: internalContext.config.entityId,
    isImpersonator: internalContext.config.isSpoofer || false, // need to figure out how to determine this
    marketplaceId: internalContext.config.marketplaceId,
  });

  return (
    <AdvertisingApiClientContext.Provider
      value={{ advertisingApiClient, advertisingApiCsrfToken }}
    >
      {children}
    </AdvertisingApiClientContext.Provider>
  );
};
